import { useContext, useCallback } from "react";
import axios from "axios";
import { SHA256, enc } from "crypto-js";
import AuthContext from "../context/AuthContext";

const API_BASE_URL = process.env.REACT_APP_API_ROUTE;

/**
 * Hook personalizado que proporciona métodos HTTP preconfigurados
 * con la ruta base y autenticación automática
 */
export const useApi = () => {
  const { token, acquirerData } = useContext(AuthContext);

  // Función para generar hash de seguridad
  const generateHash = useCallback((data, token) => {
    if (!data || !token) return { hash: null, userHash: null };

    const dataString = JSON.stringify(data);
    const hash = SHA256(dataString + token).toString(enc.Base64);
    const userHash = SHA256(dataString).toString(enc.Base64);

    return { hash, userHash };
  }, []);

  // Función para agregar headers de autenticación
  const getAuthHeaders = useCallback(() => {
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
  }, [token]);

  // Función para procesar datos con hash si es necesario
  const processData = useCallback(
    (data, addHash = false) => {
      if (!addHash || !data) return data;

      const { hash, userHash } = generateHash(data, token);
      return {
        ...data,
        reqhashMAC: hash,
        userToken: userHash,
      };
    },
    [token, generateHash]
  );

  // Método GET
  const get = useCallback(
    async (endpoint, config = {}) => {
      try {
        const url = `${API_BASE_URL}${endpoint}`;
        const headers = { ...getAuthHeaders(), ...config.headers };

        console.log(`GET request to: ${url}`);
        const response = await axios.get(url, { ...config, headers });
        return response.data;
      } catch (error) {
        console.error(`Error en GET ${endpoint}:`, error);
        throw error;
      }
    },
    [getAuthHeaders]
  );

  // Método POST
  const post = useCallback(
    async (endpoint, data = {}, config = {}, addHash = false) => {
      try {
        const url = `${API_BASE_URL}${endpoint}`;
        const processedData = processData(data, addHash);
        const headers = { ...getAuthHeaders(), ...config.headers };

        console.log(`POST request to: ${url}`, processedData);
        const response = await axios.post(url, processedData, {
          ...config,
          headers,
        });
        return response.data;
      } catch (error) {
        console.error(`Error en POST ${endpoint}:`, error);
        throw error;
      }
    },
    [processData, getAuthHeaders]
  );

  // Método PUT
  const put = useCallback(
    async (endpoint, data = {}, config = {}, addHash = false) => {
      try {
        const url = `${API_BASE_URL}${endpoint}`;
        const processedData = processData(data, addHash);
        const headers = { ...getAuthHeaders(), ...config.headers };

        console.log(`PUT request to: ${url}`, processedData);
        const response = await axios.put(url, processedData, {
          ...config,
          headers,
        });
        return response.data;
      } catch (error) {
        console.error(`Error en PUT ${endpoint}:`, error);
        throw error;
      }
    },
    [processData, getAuthHeaders]
  );

  // Método PATCH
  const patch = useCallback(
    async (endpoint, data = {}, config = {}, addHash = false) => {
      try {
        const url = `${API_BASE_URL}${endpoint}`;
        const processedData = processData(data, addHash);
        const headers = { ...getAuthHeaders(), ...config.headers };

        console.log(`PATCH request to: ${url}`, processedData);
        const response = await axios.patch(url, processedData, {
          ...config,
          headers,
        });
        return response.data;
      } catch (error) {
        console.error(`Error en PATCH ${endpoint}:`, error);
        throw error;
      }
    },
    [processData, getAuthHeaders]
  );

  // Método DELETE
  const del = useCallback(
    async (endpoint, config = {}) => {
      try {
        const url = `${API_BASE_URL}${endpoint}`;
        const headers = { ...getAuthHeaders(), ...config.headers };

        console.log(`DELETE request to: ${url}`);
        const response = await axios.delete(url, { ...config, headers });
        return response.data;
      } catch (error) {
        console.error(`Error en DELETE ${endpoint}:`, error);
        throw error;
      }
    },
    [getAuthHeaders]
  );

  // Método para requests con hash automático (compatible con el fetchData existente)
  const fetchData = useCallback(
    async (endpoint, data = {}, config = {}) => {
      return post(endpoint, data, config, true);
    },
    [post]
  );

  return {
    get,
    post,
    put,
    patch,
    delete: del,
    fetchData,
    // Información del contexto disponible
    token,
    acquirerData,
    // Función para verificar si está autenticado
    isAuthenticated: !!token,
  };
};
